<script setup lang="ts">
import type { BookingNote, ExternalBookingNote, MatchedPair, PotentialChildMatch, SchoolYear } from '@/data/kindergarten/booking-note-sync/types';
import { format, isAfter, isBefore, parse, parseISO, startOfDay } from 'date-fns';
import { CircleCheck, CirclePlus, CircleX, Download, Upload } from 'lucide';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphIcon } from '@/components/base/icon';
import { SphSelect } from '@/components/base/select';
import { SphSpinner } from '@/components/base/spinner';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { createChildAndParentUserAndBookingNote, findByKindergarten, findMatchingChildren, getExcelMetadata, importAllBookingNotes, loadExternalsViaApi, updateBookingNote, uploadExcelFile } from '@/data/kindergarten/booking-note-sync/repository';
import { BOOKING_NOTE_PEDESTAL_WEEK, BOOKING_NOTE_STATUS, EXTERNAL_BOOKING_NOTE_STATUS } from '@/data/kindergarten/booking-note-sync/types';
import { findAll as findAllSchoolYears } from '@/data/kindergarten/school-years/repository';
import { useDialog } from '@/plugins/dialog';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    kindergartenId: number
    isEditable: boolean
}>();

interface BookingListItem {
    id: string
    type: 'matched' | 'external-only'
    studentName: string
    studentNumber: string | number
    className?: string
    statusTag: 'matched' | 'new' | 'warning'
    internal?: BookingNote
    external?: ExternalBookingNote
    matchedPair?: MatchedPair
}

const { i18n } = useI18n();
const { notify } = useNotifications();
const { dialog } = useDialog();
const loading = ref(true);
const loadingExternal = ref(false);
const uploadingExcel = ref(false);
const creating = ref(false);
const bookingNotes = ref<BookingNote[]>([]);
const externalBookingNotes = ref<ExternalBookingNote[]>([]);
const showComparison = ref(false);
const selectedBookingId = ref<string | null>(null);
const searchTerm = ref('');
const filterMode = ref<'all' | 'matched' | 'external-only' | 'to-review'>('to-review');
const potentialMatches = ref<Map<number, PotentialChildMatch[]>>(new Map());
const pendingSelectionBookingId = ref<number | null>(null);
const schoolYears = ref<SchoolYear[]>([]);
const selectedSchoolYearId = ref<number | undefined>(undefined);
const fileInput = ref<HTMLInputElement | null>(null);
const detailTop = ref<HTMLElement | null>(null);
const excelFilename = ref<string | null>(null);
const excelUploadedAt = ref<string | null>(null);
const excelEntryCount = ref(0);

const dateRegex = /^(\d{4})-(\d{2})-(\d{2})/;

const schoolYearOptions = computed(() =>
    schoolYears.value.map((sy) => ({ value: sy.id, label: `${sy.name} (${sy.start} - ${sy.end})` })),
);

const matchedPairs = computed<MatchedPair[]>(() => {
    if (!externalBookingNotes.value || externalBookingNotes.value.length === 0) {
        return [];
    }
    if (!bookingNotes.value) {
        return [];
    }

    const pairs: MatchedPair[] = [];
    const externalByBookingId = new Map<string, ExternalBookingNote>();

    externalBookingNotes.value.forEach((ext) => {
        externalByBookingId.set(String(ext.bookingId), ext);
    });

    bookingNotes.value.forEach((internal) => {
        if (!internal.number) {
            return;
        }

        const external = externalByBookingId.get(internal.number);
        if (external) {
            pairs.push({
                internal,
                external,
                isEqual: isBookingNoteEqual(internal, external),
            });
        }
    });

    return pairs;
});

const unmatchedExternal = computed<ExternalBookingNote[]>(() => {
    if (!externalBookingNotes.value) {
        return [];
    }
    const matchedExternalIds = new Set(matchedPairs.value.map((p) => p.external.bookingId));
    return externalBookingNotes.value.filter((ext) => !matchedExternalIds.has(ext.bookingId));
});

const allBookingItems = computed<BookingListItem[]>(() => {
    const items: BookingListItem[] = [];

    matchedPairs.value.forEach((pair) => {
        items.push({
            id: `matched-${pair.internal.id}`,
            type: 'matched',
            studentName: pair.internal.child.fullName,
            studentNumber: pair.internal.child.studentNumber || pair.external.student.number,
            className: pair.external.class.name,
            statusTag: pair.isEqual ? 'matched' : 'warning',
            internal: pair.internal,
            external: pair.external,
            matchedPair: pair,
        });
    });

    unmatchedExternal.value.forEach((ext) => {
        items.push({
            id: `ext-${ext.bookingId}`,
            type: 'external-only',
            studentName: `${ext.student.firstName} ${ext.student.lastName}`,
            studentNumber: ext.student.number,
            className: ext.class.name,
            statusTag: 'new',
            external: ext,
        });
    });

    return items;
});

const filteredBookingItems = computed<BookingListItem[]>(() => {
    let filtered = allBookingItems.value;

    if (filterMode.value !== 'all') {
        filtered = filtered.filter((item) => {
            if (filterMode.value === 'matched') {
                return item.type === 'matched';
            }
            if (filterMode.value === 'external-only') {
                return item.type === 'external-only';
            }
            if (filterMode.value === 'to-review') {
                return item.type === 'external-only'
                    || (item.type === 'matched' && item.external?.cancelled && item.internal?.status !== BOOKING_NOTE_STATUS.CANCELED);
            }
            return true;
        });
    }

    if (searchTerm.value) {
        const search = searchTerm.value.toLowerCase();
        filtered = filtered.filter((item) =>
            item.studentName.toLowerCase().includes(search)
            || String(item.studentNumber).toLowerCase().includes(search),
        );
    }

    return filtered;
});

const selectedBookingDetail = computed<BookingListItem | null>(() => {
    if (!selectedBookingId.value) {
        return null;
    }
    return allBookingItems.value.find((item) => item.id === selectedBookingId.value) || null;
});

const filterCounts = computed(() => {
    const toReviewCount = allBookingItems.value.filter((item) => {
        if (item.type === 'external-only') {
            return true;
        }
        if (item.type === 'matched' && item.external?.cancelled && item.internal?.status !== BOOKING_NOTE_STATUS.CANCELED) {
            return true;
        }
        return false;
    }).length;

    return {
        total: allBookingItems.value.length,
        matched: allBookingItems.value.filter((item) => item.type === 'matched').length,
        externalOnly: allBookingItems.value.filter((item) => item.type === 'external-only').length,
        toReview: toReviewCount,
    };
});

onMounted(() => {
    loadSchoolYears();
});

watch(selectedSchoolYearId, (newValue, oldValue) => {
    if (oldValue !== undefined && newValue !== undefined) {
        load(true);
    }
});

function loadSchoolYears() {
    loading.value = true;
    findAllSchoolYears(props.kindergartenId)
        .then((response: SchoolYear[]) => {
            schoolYears.value = response;
            if (response.length > 0) {
                const currentSchoolYear = findCurrentSchoolYear(response);
                selectedSchoolYearId.value = currentSchoolYear?.id ?? response[0].id;
                load(true);
            } else {
                loading.value = false;
            }
        })
        .catch(() => {
            notify.error('Fehler beim Laden der Schuljahre');
            loading.value = false;
        });
}

function findCurrentSchoolYear(years: SchoolYear[]): SchoolYear | undefined {
    const today = startOfDay(new Date());
    return years.find((sy) => {
        const start = startOfDay(parse(sy.start, 'dd.MM.yyyy', new Date()));
        const end = startOfDay(parse(sy.end, 'dd.MM.yyyy', new Date()));
        return !isAfter(start, today) && !isBefore(end, today);
    });
}

function formatDate(dateString: string | null | undefined): string {
    if (!dateString) {
        return '--';
    }
    const match = dateString.match(dateRegex);
    if (match) {
        return `${match[3]}.${match[2]}.${match[1]}`;
    }
    return dateString;
}

function formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
        return '--';
    }
    if (typeof value === 'boolean') {
        return value ? i18n.t('Ja') : i18n.t('Nein');
    }
    if (typeof value === 'number') {
        return String(value);
    }
    return String(value);
}

function hasTimeSlot(times: Record<number, string[]> | undefined, timeSlot: string): boolean | undefined {
    if (!times) {
        return undefined;
    }
    return Object.values(times).some((slots) => slots.includes(timeSlot));
}

function compareValues(externalValue: any, internalValue: any, field?: string, externalBookingNote?: ExternalBookingNote): boolean {
    if (field === 'status' && externalBookingNote) {
        if (externalValue === EXTERNAL_BOOKING_NOTE_STATUS.CURRENT
            && externalBookingNote.cancelled
            && internalValue === BOOKING_NOTE_STATUS.CANCELED) {
            return true;
        }
        return externalValue === EXTERNAL_BOOKING_NOTE_STATUS.CURRENT
            && !externalBookingNote.cancelled
            && internalValue === BOOKING_NOTE_STATUS.NEW;
    }

    if (externalValue === null || externalValue === undefined || externalValue === '') {
        return internalValue === null || internalValue === undefined || internalValue === '';
    }
    return String(externalValue) === String(internalValue);
}

function comparisonIcon(externalValue: any, internalValue: any, field?: string, externalBookingNote?: ExternalBookingNote) {
    return compareValues(externalValue, internalValue, field, externalBookingNote) ? CircleCheck : CircleX;
}

function comparisonClass(externalValue: any, internalValue: any, field?: string, externalBookingNote?: ExternalBookingNote) {
    return compareValues(externalValue, internalValue, field, externalBookingNote) ? 'status-match' : 'status-mismatch';
}

function load(initial = false) {
    if (selectedSchoolYearId.value === undefined) {
        return;
    }
    loading.value = initial;
    findByKindergarten(props.kindergartenId, selectedSchoolYearId.value)
        .then((response) => {
            bookingNotes.value = response;
            if (initial) {
                loadExternalBookingNotes();
            }
        })
        .catch(() => {
            notify.error('error.loadingData');
        })
        .finally(() => {
            loading.value = false;
        });
}

async function loadExternalBookingNotes() {
    loadingExternal.value = true;
    try {
        const [response, metadata] = await Promise.all([
            loadExternalsViaApi(props.kindergartenId, selectedSchoolYearId.value!),
            getExcelMetadata(props.kindergartenId),
        ]);
        externalBookingNotes.value = response;
        showComparison.value = true;
        excelFilename.value = metadata.filename;
        excelUploadedAt.value = metadata.uploadedAt
            ? format(parseISO(metadata.uploadedAt), 'dd.MM.yyyy HH:mm')
            : null;
        excelEntryCount.value = metadata.entryCount;
    } catch {
        // Silently fail - no persisted data available
    } finally {
        loadingExternal.value = false;
    }
}

function triggerFileUpload() {
    fileInput.value?.click();
}

async function handleFileUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
        return;
    }

    uploadingExcel.value = true;
    try {
        await uploadExcelFile(props.kindergartenId, file);
        excelFilename.value = file.name;
        excelUploadedAt.value = format(new Date(), 'dd.MM.yyyy HH:mm');
        await loadExternalBookingNotes();
        notify.success('Die Datei wurde hochgeladen');
    } catch {
        notify.error('Fehler beim Importieren der Excel-Datei');
    } finally {
        uploadingExcel.value = false;
        input.value = '';
    }
}

async function selectPendingBooking() {
    if (pendingSelectionBookingId.value === null) {
        return;
    }
    const bookingId = pendingSelectionBookingId.value;
    pendingSelectionBookingId.value = null;

    await nextTick();

    const item = allBookingItems.value.find(
        (i) => i.external?.bookingId === bookingId || i.internal?.number === String(bookingId),
    );
    if (item) {
        selectBooking(item.id);
    }
}

function takeExternalChanges(bookingNoteId: number, externalBookingNote: ExternalBookingNote) {
    updateBookingNote(bookingNoteId, externalBookingNote)
        .then(() => {
            load(false);
            notify.success('Buchungsmitteilung wurde aktualisiert');
        })
        .catch(() => notify.error('Fehler beim Aktualisieren der Buchungsmitteilung'));
}

async function createBookingNoteAndChildAndParentUser(externalBookingNote: ExternalBookingNote): Promise<void> {
    creating.value = true;
    pendingSelectionBookingId.value = externalBookingNote.bookingId;
    try {
        await createChildAndParentUserAndBookingNote(props.kindergartenId, externalBookingNote);
        const response = await findByKindergarten(props.kindergartenId, selectedSchoolYearId.value!);
        bookingNotes.value = response;
        await selectPendingBooking();
        notify.success('Kind, Bezugsperson und Buchungsmitteilung wurden angelegt');
    } catch {
        pendingSelectionBookingId.value = null;
        notify.error('Fehler beim Anlegen von Kind, Bezugsperson und Buchungsmitteilung');
    } finally {
        creating.value = false;
    }
}

async function searchForMatches(external: ExternalBookingNote) {
    if (potentialMatches.value.has(external.bookingId)) {
        return;
    }

    const localMatches: PotentialChildMatch[] = [];
    const seenChildIds = new Set<number>();

    if (bookingNotes.value) {
        bookingNotes.value.forEach((bookingNote) => {
            if (bookingNote.child.studentNumber
                && String(bookingNote.child.studentNumber) === String(external.student.number)
                && !seenChildIds.has(bookingNote.child.id)) {
                seenChildIds.add(bookingNote.child.id);
                localMatches.push({
                    id: bookingNote.child.id,
                    firstName: bookingNote.child.firstName,
                    lastName: bookingNote.child.lastName,
                    dateOfBirth: bookingNote.child.dateOfBirth || '',
                    studentNumber: bookingNote.child.studentNumber,
                    isExactMatch: true,
                });
            }
        });
    }

    if (localMatches.length > 0) {
        potentialMatches.value.set(external.bookingId, localMatches);
        return;
    }

    try {
        const matches = await findMatchingChildren(
            props.kindergartenId,
            external.student.firstName,
            external.student.lastName,
            external.student.dateOfBirth,
        );
        potentialMatches.value.set(external.bookingId, matches);
    } catch (error) {
        console.error('Error searching for matches:', error);
    }
}

function getPotentialMatches(external: ExternalBookingNote): PotentialChildMatch[] {
    return potentialMatches.value.get(external.bookingId) || [];
}

function hasExactMatch(external: ExternalBookingNote): boolean {
    const matches = getPotentialMatches(external);
    return matches.some((match) => match.isExactMatch);
}

function linkToChildBookingNotes(childId: number): string {
    return `/backend/child/${childId}/booking-note`;
}

function isBookingNoteEqual(internal: BookingNote, external: ExternalBookingNote): boolean {
    if (external.student.dateOfBirth !== internal.child.dateOfBirth) {
        return false;
    }
    if (external.student.dateOfBirth !== internal.child.dateOfBirth) {
        return false;
    }
    if (internal.parentUser && internal.parentUser.fullName === external.guardian.fullName) {
        if (external.address.street !== internal.parentUser.street) {
            return false;
        }
        if (external.address.zipCode !== internal.parentUser.zip) {
            return false;
        }
        if (external.address.city !== internal.parentUser.city) {
            return false;
        }
        if (external.contact.email !== internal.parentUser.email) {
            return false;
        }
        if (external.contact.phone !== internal.parentUser.phone) {
            return false;
        }
    }
    if (external.cancelled && internal.status !== BOOKING_NOTE_STATUS.CANCELED) {
        return false;
    }
    if (internal.start !== external.period.start || internal.end !== external.period.end) {
        return false;
    }
    return true;
}

function navigateToPrevious() {
    if (!selectedBookingId.value) {
        return;
    }
    const currentIndex = filteredBookingItems.value.findIndex((item) => item.id === selectedBookingId.value);
    if (currentIndex > 0) {
        selectBooking(filteredBookingItems.value[currentIndex - 1].id);
    }
}

function navigateToNext() {
    if (!selectedBookingId.value) {
        return;
    }
    const currentIndex = filteredBookingItems.value.findIndex((item) => item.id === selectedBookingId.value);
    if (currentIndex < filteredBookingItems.value.length - 1) {
        selectBooking(filteredBookingItems.value[currentIndex + 1].id);
    }
}

function hasPrevious(): boolean {
    if (!selectedBookingId.value) {
        return false;
    }
    const currentIndex = filteredBookingItems.value.findIndex((item) => item.id === selectedBookingId.value);
    return currentIndex > 0;
}

function hasNext(): boolean {
    if (!selectedBookingId.value) {
        return false;
    }
    const currentIndex = filteredBookingItems.value.findIndex((item) => item.id === selectedBookingId.value);
    return currentIndex < filteredBookingItems.value.length - 1;
}

function selectBooking(bookingId: string) {
    selectedBookingId.value = bookingId;
    const item = allBookingItems.value.find((i) => i.id === bookingId);

    if (item?.type === 'external-only' && item.external) {
        searchForMatches(item.external);
    }

    nextTick(() => {
        detailTop.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
}

function setFilterMode(mode: 'all' | 'matched' | 'external-only' | 'to-review') {
    filterMode.value = mode;
}

const importingAll = ref(false);

async function confirmTakeAllData() {
    const confirmed = await dialog.confirm({
        title: i18n.t('Alle Daten übernehmen'),
        message: i18n.t('Sollen alle {count} Buchungsmitteilungen verarbeitet werden? Dabei werden ggf. neue Kinder, Bezugspersonen und Buchungsmitteilungen angelegt und bestehende aktualisiert.', { count: excelEntryCount.value }),
        confirmButtonLabel: i18n.t('Alle übernehmen'),
    });

    if (!confirmed) {
        return;
    }

    importingAll.value = true;
    try {
        await importAllBookingNotes(props.kindergartenId);
        notify.success('Alle Daten wurden übernommen');
        load(true);
    } catch {
        notify.error('Fehler beim Übernehmen der Daten');
    } finally {
        importingAll.value = false;
    }
}
</script>

<template>
    <div class="school-year-filter">
        <label>{{ i18n.t('Schuljahr') }}</label>
        <SphSelect
            v-model="selectedSchoolYearId"
            :options="schoolYearOptions"
            :null-option="false"
            class="w-96"
        />
        <input
            ref="fileInput"
            accept=".xlsx"
            style="display: none"
            type="file"
            @change="handleFileUpload"
        />
        <SphButton
            :icon="Upload"
            :label="i18n.t('Excel-Datei importieren')"
            :disabled="importingAll"
            :loading="uploadingExcel"
            variant="primary"
            @click="triggerFileUpload"
        />
        <div
            v-if="excelFilename"
            class="excel-filename"
        >
            {{ i18n.t('Excel-Datei') }}: {{ excelFilename }}<br />
            {{ i18n.t('Hochgeladen am') }}: {{ excelUploadedAt }}
        </div>
        <div class="spacer"></div>
        <SphButton
            :disabled="loading || uploadingExcel || !excelFilename"
            :label="i18n.t('Alle Daten übernehmen')"
            :loading="importingAll"
            variant="primary"
            @click="confirmTakeAllData"
        />
    </div>
    <div class="booking-notes-comparison">
        <SphSpinner
            v-if="loading"
            :show="loading"
            variant="centered"
        />
        <div
            v-show="!loading"
            class="app"
        >
            <aside class="sidebar">
                <div class="search">
                    <div class="search-input-wrapper">
                        <input
                            v-model="searchTerm"
                            :placeholder="i18n.t('Kind suchen...')"
                            type="text"
                        />
                        <button
                            v-if="searchTerm"
                            type="button"
                            class="search-clear"
                            @click="searchTerm = ''"
                        >
                            &times;
                        </button>
                    </div>
                </div>

                <div class="filters">
                    <span
                        class="chip"
                        :class="{ active: filterMode === 'to-review' }"
                        @click="setFilterMode('to-review')"
                    >
                        {{ i18n.t('Zu prüfen') }} ({{ filterCounts.toReview }})
                    </span>
                    <span
                        class="chip"
                        :class="{ active: filterMode === 'external-only' }"
                        @click="setFilterMode('external-only')"
                    >
                        {{ i18n.t('Neu') }} ({{ filterCounts.externalOnly }})
                    </span>
                    <span
                        class="chip"
                        :class="{ active: filterMode === 'all' }"
                        @click="setFilterMode('all')"
                    >
                        {{ i18n.t('Alle') }} ({{ filterCounts.total }})
                    </span>
                    <span
                        class="chip"
                        :class="{ active: filterMode === 'matched' }"
                        @click="setFilterMode('matched')"
                    >
                        {{ i18n.t('Zugeordnet') }} ({{ filterCounts.matched }})
                    </span>
                </div>

                <div class="list">
                    <div
                        v-if="showComparison && externalBookingNotes.length === 0"
                        class="list-empty-state"
                    >
                        {{ i18n.t('Es wurden keine externen Buchungsmitteilungen für dieses Schuljahr gefunden.') }}
                    </div>
                    <div
                        v-for="item in filteredBookingItems"
                        :key="item.id"
                        class="row-item"
                        :class="{ active: selectedBookingId === item.id }"
                        @click="selectBooking(item.id)"
                    >
                        <div class="who">
                            <div class="name">
                                {{ item.studentName }}
                            </div>
                            <div class="sub">
                                #{{ item.studentNumber }}<template v-if="item.className">
                                    · Klasse {{ item.className }}
                                </template>
                            </div>
                        </div>
                        <span
                            v-if="item.type === 'matched'"
                            class="tag matched"
                        >{{ i18n.t('Zugeordnet') }}</span>
                        <span
                            v-else-if="item.type === 'external-only'"
                            class="tag neu"
                        >{{ i18n.t('Neu') }}</span>
                        <span
                            v-if="item.external?.cancelled"
                            class="tag storno"
                        >{{ i18n.t('Storniert') }}</span>
                    </div>
                </div>
            </aside>

            <main class="main">
                <template v-if="!selectedBookingDetail">
                    <div class="empty-state">
                        {{ i18n.t('Wählen Sie links einen Datensatz aus.') }}
                    </div>
                </template>

                <template v-else>
                    <div ref="detailTop"></div>
                    <div
                        v-if="selectedBookingDetail.type === 'external-only' && selectedBookingDetail.external"
                        class="banner"
                        :class="hasExactMatch(selectedBookingDetail.external) ? 'matched' : 'neu'"
                    >
                        <template v-if="hasExactMatch(selectedBookingDetail.external)">
                            {{ i18n.t('Kind eindeutig identifiziert (Schülernummer)') }}
                        </template>
                        <template v-else-if="getPotentialMatches(selectedBookingDetail.external).length > 0">
                            {{ i18n.t('Mögliche Übereinstimmungen gefunden (Name/Geburtsdatum)') }}
                        </template>
                        <template v-else>
                            {{ i18n.t('Dieses Kind ist nicht in KigaRoo vorhanden. Ein neuer Datensatz wird angelegt.') }}
                        </template>
                    </div>
                    <div
                        v-else-if="selectedBookingDetail.external && selectedBookingDetail.external.cancelled"
                        class="banner storno"
                    >
                        {{ i18n.t('Die Buchung wurde vom Träger storniert') }}
                    </div>

                    <div class="main-head">
                        <div>
                            <h2>
                                <a
                                    v-if="selectedBookingDetail?.internal"
                                    class="kgr-link"
                                    :href="linkToChildBookingNotes(selectedBookingDetail.internal.child.id)"
                                >
                                    {{ selectedBookingDetail.studentName }}
                                </a>
                                <span v-else>
                                    {{ selectedBookingDetail.studentName }}
                                </span>
                            </h2>
                            <div class="meta">
                                {{ i18n.t?.('Schüler-Nr.') }} {{ selectedBookingDetail.studentNumber }}
                                <template v-if="selectedBookingDetail.className">
                                    · {{ i18n.t('Klasse') }} {{ selectedBookingDetail.className }}
                                </template>
                                <template v-if="selectedBookingDetail.external || selectedBookingDetail.internal">
                                    · {{ i18n.t('Buchungsnummer') }}
                                    {{ selectedBookingDetail.external?.bookingId || selectedBookingDetail.internal?.number }}
                                </template>
                            </div>
                        </div>
                        <div class="summary-pills">
                            <span
                                v-if="selectedBookingDetail.type === 'matched'"
                                class="pill ok"
                            >{{ i18n.t('Zugeordnet') }}</span>
                            <span
                                v-else-if="selectedBookingDetail.type === 'external-only'"
                                class="pill new"
                            >{{ i18n.t('Neu') }}</span>
                            <span
                                v-if="selectedBookingDetail.external?.cancelled"
                                class="pill storno"
                            >{{ i18n.t('Storniert') }}</span>
                        </div>
                    </div>

                    <div class="toolbar">
                        <div class="toolbar-left">
                            <SphButton
                                :label="i18n.t('← Vorheriger')"
                                size="sm"
                                :disabled="!hasPrevious()"
                                @click="navigateToPrevious"
                            />
                            <SphButton
                                :label="i18n.t('Nächster →')"
                                size="sm"
                                :disabled="!hasNext()"
                                @click="navigateToNext"
                            />
                        </div>
                        <SphButton
                            v-if="selectedBookingDetail.type === 'external-only' && selectedBookingDetail.external"
                            :label="i18n.t('Buchungsmitteilung anlegen')"
                            :icon="CirclePlus"
                            variant="primary"
                            :disabled="creating"
                            @click="createBookingNoteAndChildAndParentUser(selectedBookingDetail.external)"
                        />
                        <SphButton
                            v-if="selectedBookingDetail.type === 'matched' && selectedBookingDetail.matchedPair && !selectedBookingDetail.matchedPair.isEqual"
                            :label="i18n.t('Änderungen vom Träger übernehmen')"
                            :icon="Download"
                            variant="primary"
                            :disabled="creating"
                            @click="takeExternalChanges(selectedBookingDetail.internal!.id, selectedBookingDetail.external!)"
                        />
                    </div>

                    <div
                        v-if="selectedBookingDetail.type === 'external-only' && selectedBookingDetail.external && getPotentialMatches(selectedBookingDetail.external).length > 0"
                        class="potential-matches"
                        :class="hasExactMatch(selectedBookingDetail.external) ? 'exact-match' : ''"
                    >
                        <h3>{{ hasExactMatch(selectedBookingDetail.external) ? i18n.t('Identifiziertes Kind') : i18n.t('Mögliche Übereinstimmungen') }}</h3>
                        <div class="match-list">
                            <div
                                v-for="match in getPotentialMatches(selectedBookingDetail.external)"
                                :key="match.id"
                                class="match-item"
                            >
                                <div class="match-info">
                                    <strong>{{ match.firstName }} {{ match.lastName }}</strong>
                                    <div class="meta">
                                        {{ i18n.t('Geburtsdatum') }}: {{ formatDate(match.dateOfBirth) }}
                                        <template v-if="match.studentNumber">
                                            · {{ i18n.t('Schüler-Nr.') }} {{ match.studentNumber }}
                                        </template>
                                        <template v-if="match.isExactMatch">
                                            · <span class="exact-badge">{{ i18n.t('Exakte Übereinstimmung') }}</span>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p class="hint">
                            <template v-if="hasExactMatch(selectedBookingDetail.external)">
                                {{ i18n.t('Dieses Kind wurde über die Schülernummer eindeutig identifiziert. Die Buchungsmitteilung wird automatisch verknüpft.') }}
                            </template>
                            <template v-else>
                                {{ i18n.t('Die Buchungsmitteilung wird automatisch mit dem passenden Kind verknüpft, falls genau eine Übereinstimmung gefunden wurde.') }}
                            </template>
                        </p>
                    </div>

                    <!-- Stammdaten -->
                    <div class="group">
                        <div class="group-head">
                            {{ i18n.t('Stammdaten') }}
                        </div>
                        <table class="fields">
                            <thead>
                                <tr>
                                    <th>{{ i18n.t('Feld') }}</th>
                                    <th>{{ i18n.t('KigaRoo') }}</th>
                                    <th>{{ i18n.t('Quelle') }}</th>
                                    <th>{{ i18n.t('Status') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Vorname') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.child.firstName) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.student.firstName) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.student.firstName, selectedBookingDetail.internal?.child.firstName)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.student.firstName, selectedBookingDetail.internal?.child.firstName)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Nachname') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.child.lastName) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.student.lastName) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.student.lastName, selectedBookingDetail.internal?.child.lastName)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.student.lastName, selectedBookingDetail.internal?.child.lastName)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Geburtsdatum') }}
                                    </td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.internal?.child.dateOfBirth) }}</span></td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.external?.student.dateOfBirth) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.student.dateOfBirth, selectedBookingDetail.internal?.child.dateOfBirth)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.student.dateOfBirth, selectedBookingDetail.internal?.child.dateOfBirth)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Schüler-Nummer') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.child.studentNumber) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.student.number) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.student.number, selectedBookingDetail.internal?.child.studentNumber)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.student.number, selectedBookingDetail.internal?.child.studentNumber)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Primäre Kontaktperson') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.parentUser?.fullName) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.guardian.fullName) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.guardian.fullName, selectedBookingDetail.internal?.parentUser?.fullName)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.guardian.fullName, selectedBookingDetail.internal?.parentUser?.fullName)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Straße') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.parentUser?.street) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.address.street ? `${selectedBookingDetail.external.address.street} ${selectedBookingDetail.external.address.houseNumber || ''}`.trim() : null) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.address.street ? `${selectedBookingDetail.external.address.street} ${selectedBookingDetail.external.address.houseNumber || ''}`.trim() : null, selectedBookingDetail.internal?.parentUser?.street)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.address.street ? `${selectedBookingDetail.external.address.street} ${selectedBookingDetail.external.address.houseNumber || ''}`.trim() : null, selectedBookingDetail.internal?.parentUser?.street)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('PLZ') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.parentUser?.zip) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.address.zipCode) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.address.zipCode, selectedBookingDetail.internal?.parentUser?.zip)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.address.zipCode, selectedBookingDetail.internal?.parentUser?.zip)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Ort') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.parentUser?.city) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.address.city) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.address.city, selectedBookingDetail.internal?.parentUser?.city)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.address.city, selectedBookingDetail.internal?.parentUser?.city)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('E-Mail') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.parentUser?.email) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.contact.email) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.contact.email, selectedBookingDetail.internal?.parentUser?.email)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.contact.email, selectedBookingDetail.internal?.parentUser?.email)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Telefon') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.parentUser?.phone) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.contact.phone) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.contact.phone, selectedBookingDetail.internal?.parentUser?.phone)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.contact.phone, selectedBookingDetail.internal?.parentUser?.phone)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Buchungsdetails -->
                    <div class="group">
                        <div class="group-head">
                            {{ i18n.t('Buchungsdetails') }}
                        </div>
                        <table class="fields">
                            <thead>
                                <tr>
                                    <th>{{ i18n.t('Feld') }}</th>
                                    <th>KigaRoo</th>
                                    <th>{{ i18n.t('Quelle') }}</th>
                                    <th>{{ i18n.t('Status') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Buchungsnummer') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.number) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.bookingId) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.bookingId, selectedBookingDetail.internal?.number)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.bookingId, selectedBookingDetail.internal?.number)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Status') }}
                                    </td>
                                    <td><span class="val">{{ i18n.t(formatValue(selectedBookingDetail.internal?.status)) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.status) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.status, selectedBookingDetail.internal?.status, 'status', selectedBookingDetail.external)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.status, selectedBookingDetail.internal?.status, 'status', selectedBookingDetail.external)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Storniert') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(null) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.cancelled) }}</span></td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Typ') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(null) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.type) }}</span></td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Erfasst am') }}
                                    </td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.internal?.bookingDate) }}</span></td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.external?.recordedAt) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.recordedAt, selectedBookingDetail.internal?.bookingDate)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.recordedAt, selectedBookingDetail.internal?.bookingDate)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Zeitraum -->
                    <div class="group">
                        <div class="group-head">
                            {{ i18n.t('Zeitraum') }}
                        </div>
                        <table class="fields">
                            <thead>
                                <tr>
                                    <th>{{ i18n.t('Feld') }}</th>
                                    <th>KigaRoo</th>
                                    <th>{{ i18n.t('Quelle') }}</th>
                                    <th>{{ i18n.t('Status') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Von') }}
                                    </td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.internal?.start) }}</span></td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.external?.period.start) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.period.start, selectedBookingDetail.internal?.start)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.period.start, selectedBookingDetail.internal?.start)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Bis') }}
                                    </td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.internal?.end) }}</span></td>
                                    <td><span class="val">{{ formatDate(selectedBookingDetail.external?.period.end) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.period.end, selectedBookingDetail.internal?.end)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.period.end, selectedBookingDetail.internal?.end)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Schuljahr') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.schoolYear.name) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.schoolYear) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.schoolYear, selectedBookingDetail.internal?.schoolYear.name)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.schoolYear, selectedBookingDetail.internal?.schoolYear.name)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Betreuungsmodule -->
                    <div class="group">
                        <div class="group-head">
                            {{ i18n.t('Betreuungsmodule') }}
                        </div>
                        <table class="fields">
                            <thead>
                                <tr>
                                    <th>{{ i18n.t('Feld') }}</th>
                                    <th>KigaRoo</th>
                                    <th>{{ i18n.t('Quelle') }}</th>
                                    <th>{{ i18n.t('Status') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Früh 06-07') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(hasTimeSlot(selectedBookingDetail.internal?.times, '6-7')) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.early0607) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.early0607, hasTimeSlot(selectedBookingDetail.internal?.times, '6-7'))">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.early0607, hasTimeSlot(selectedBookingDetail.internal?.times, '6-7'))"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Früh 07-08') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(hasTimeSlot(selectedBookingDetail.internal?.times, '7-8')) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.early0708) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.early0708, hasTimeSlot(selectedBookingDetail.internal?.times, '7-8'))">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.early0708, hasTimeSlot(selectedBookingDetail.internal?.times, '7-8'))"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Kernzeit') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(hasTimeSlot(selectedBookingDetail.internal?.times, '13-16')) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.coreTime) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.coreTime, hasTimeSlot(selectedBookingDetail.internal?.times, '13-16'))">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.coreTime, hasTimeSlot(selectedBookingDetail.internal?.times, '13-16'))"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Spät 16-17') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(hasTimeSlot(selectedBookingDetail.internal?.times, '16-17')) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.late1617) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.late1617, hasTimeSlot(selectedBookingDetail.internal?.times, '16-17'))">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.late1617, hasTimeSlot(selectedBookingDetail.internal?.times, '16-17'))"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Spät 17-18') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(hasTimeSlot(selectedBookingDetail.internal?.times, '17-18')) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.late1718) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.late1718, hasTimeSlot(selectedBookingDetail.internal?.times, '17-18'))">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.late1718, hasTimeSlot(selectedBookingDetail.internal?.times, '17-18'))"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Sockelwoche 08-16') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal ? selectedBookingDetail.internal.pedestalWeek === BOOKING_NOTE_PEDESTAL_WEEK.STANDARD : undefined) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.baseWeek0816) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.baseWeek0816, selectedBookingDetail.internal ? selectedBookingDetail.internal.pedestalWeek === BOOKING_NOTE_PEDESTAL_WEEK.STANDARD : undefined)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.baseWeek0816, selectedBookingDetail.internal ? selectedBookingDetail.internal.pedestalWeek === BOOKING_NOTE_PEDESTAL_WEEK.STANDARD : undefined)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Sockelwoche 06-18') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal ? selectedBookingDetail.internal.pedestalWeek === BOOKING_NOTE_PEDESTAL_WEEK.EXTENDED : undefined) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.baseWeek0618) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.baseWeek0618, selectedBookingDetail.internal ? selectedBookingDetail.internal.pedestalWeek === BOOKING_NOTE_PEDESTAL_WEEK.EXTENDED : undefined)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.baseWeek0618, selectedBookingDetail.internal ? selectedBookingDetail.internal.pedestalWeek === BOOKING_NOTE_PEDESTAL_WEEK.EXTENDED : undefined)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Ferienwochen 08-16') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.numStandardWeeks) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.holidayWeeks0816) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.holidayWeeks0816, selectedBookingDetail.internal?.numStandardWeeks)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.holidayWeeks0816, selectedBookingDetail.internal?.numStandardWeeks)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Ferienwochen 06-18') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.numExtendedWeeks) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.holidayWeeks0618) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.holidayWeeks0618, selectedBookingDetail.internal?.numExtendedWeeks)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.holidayWeeks0618, selectedBookingDetail.internal?.numExtendedWeeks)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Mittagsverpflegung') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.lunch) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.lunchCatering) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.lunchCatering, selectedBookingDetail.internal?.lunch)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.lunchCatering, selectedBookingDetail.internal?.lunch)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-label">
                                        {{ i18n.t('Inklusion') }}
                                    </td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.internal?.inclusion) }}</span></td>
                                    <td><span class="val">{{ formatValue(selectedBookingDetail.external?.modules.inclusion) }}</span></td>
                                    <td :class="comparisonClass(selectedBookingDetail.external?.modules.inclusion, selectedBookingDetail.internal?.inclusion)">
                                        <SphIcon
                                            :icon="comparisonIcon(selectedBookingDetail.external?.modules.inclusion, selectedBookingDetail.internal?.inclusion)"
                                            size="sm"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-if="selectedBookingDetail.type === 'external-only' && selectedBookingDetail.external"
                        class="action-buttons"
                    >
                        <SphButton
                            :label="i18n.t('Buchungsmitteilung anlegen')"
                            :icon="CirclePlus"
                            variant="primary"
                            :disabled="creating"
                            @click="createBookingNoteAndChildAndParentUser(selectedBookingDetail.external)"
                        />
                    </div>

                    <div
                        v-else-if="selectedBookingDetail.type === 'matched' && selectedBookingDetail.matchedPair && !selectedBookingDetail.matchedPair.isEqual"
                        class="action-buttons"
                    >
                        <SphButton
                            :label="i18n.t('Änderungen vom Träger übernehmen')"
                            :icon="Download"
                            variant="primary"
                            :disabled="creating"
                            @click="takeExternalChanges(selectedBookingDetail.internal!.id, selectedBookingDetail.external!)"
                        />
                    </div>
                </template>
            </main>
        </div>
    </div>
</template>

<style scoped lang="scss">
.school-year-filter {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #dde0e5;
    border-radius: 4px;

    label {
        font-size: 14px;
        font-weight: 600;
        color: #1b1f6b;
    }

    select {
        padding: 8px 12px;
        border: 1px solid #dde0e5;
        border-radius: 4px;
        font-size: 14px;
        min-width: 200px;
        background: #f0f1f3;
        cursor: pointer;

        &:focus {
            outline: none;
            border-color: #1b1f6b;
        }
    }
}

.booking-notes-comparison {
    width: 100%;
    min-height: 600px;
    --navy: #1b1f6b;
    --navy-soft: #282d8a;
    --accent: #1b1f6b;
    --accent-soft: #e4e5f2;
    --stripe: #e6e9ec;
    --warn: #b5482f;
    --warn-soft: #fbe9e4;
    --ok: #2f7a4d;
    --ok-soft: #e7f4ea;
    --new: #2f5fa0;
    --new-soft: #e5edf7;
    --line: #dde0e5;
    --panel: #ffffff;
    --bg: #f0f1f3;
    --ink: #28303e;
    --ink-soft: #6b7280;
    --storno: #8a4fb3;
    --storno-soft: #f0e7f7;
}

.comparison-controls {
    margin-bottom: 1.5rem;
    margin-left: 14px;
    width: calc(340px - 28px);

    :deep(button) {
        width: 100%;
    }
}

.excel-filename {
    margin-top: 0.5rem;
    margin-left: 14px;
    font-size: 12px;
    color: #6b7280;
    word-break: break-all;
}

.spacer {
    flex: 1;
}

.loading,
.empty-state {
    padding: 80px 20px;
    text-align: center;
    color: var(--ink-soft);
    font-size: 14px;
}

.app {
    display: grid;
    grid-template-columns: 340px 1fr;
    gap: 0;
    min-height: 600px;
}

.sidebar {
    background: var(--panel);
    border-right: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.search {
    padding: 14px;
    border-bottom: 1px solid var(--line);

    .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
    }

    input {
        width: 100%;
        padding: 7px 28px 7px 10px;
        border: 1px solid var(--line);
        border-radius: 4px;
        font-size: 13px;
        background: var(--bg);
    }

    .search-clear {
        position: absolute;
        right: 6px;
        width: 18px;
        height: 18px;
        padding: 0;
        border: none;
        background: var(--ink-soft);
        color: #fff;
        border-radius: 50%;
        font-size: 14px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            background: var(--ink);
        }
    }
}

.filters {
    display: flex;
    gap: 6px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--line);
    flex-wrap: wrap;
}

.chip {
    border: 1px solid var(--line);
    background: var(--bg);
    padding: 4px 9px;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    user-select: none;

    &.active {
        background: var(--accent);
        color: #fff;
        border-color: var(--accent);
    }

    &:hover:not(.active) {
        background: var(--accent-soft);
    }
}

.list {
    overflow-y: auto;
    flex: 1;
}

.list-empty-state {
    padding: 40px 20px;
    text-align: center;
    color: #6b7280;
    font-size: 13px;
}

.row-item {
    padding: 9px 14px;
    border-bottom: 1px solid var(--line);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    &:nth-child(even) {
        background: var(--stripe);
    }

    &:hover {
        background: var(--accent-soft);
    }

    &.active {
        background: var(--accent-soft);
        box-shadow: inset 3px 0 0 var(--navy);
    }

    .who {
        flex: 1;
        min-width: 0;
    }

    .name {
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--navy);
    }

    .sub {
        font-size: 11px;
        color: var(--ink-soft);
    }
}

.tag {
    flex: none;
    font-size: 9.5px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.02em;

    &.neu {
        background: var(--new-soft);
        color: var(--new);
    }

    &.matched {
        background: var(--ok-soft);
        color: var(--ok);
    }

    &.warning {
        background: var(--warn-soft);
        color: var(--warn);
    }

    &.storno {
        background: var(--storno-soft);
        color: var(--storno);
    }
}

.main {
    overflow-y: auto;
    padding: 24px 32px 60px;
}

.banner {
    padding: 12px 16px;
    border-radius: 4px;
    font-size: 13px;
    margin-bottom: 16px;
    display: flex;
    gap: 8px;
    align-items: flex-start;

    &.storno {
        background: var(--storno-soft);
        color: var(--storno);
        border: 1px solid #d9c3ea;
    }

    &.neu {
        background: var(--new-soft);
        color: var(--new);
        border: 1px solid #c6d7ec;
    }

    &.warning {
        background: var(--warn-soft);
        color: var(--warn);
        border: 1px solid #e6b3a3;
    }

    &.matched {
        background: var(--ok-soft);
        color: var(--ok);
        border: 1px solid var(--ok);
    }
}

.main-head {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 100px;
    margin-bottom: 14px;
    flex-wrap: wrap;

    h2 {
        margin: 0 0 4px;
        font-size: 18px;
        color: var(--navy);
        font-weight: 700;
    }

    .meta {
        font-size: 12px;
        color: var(--ink-soft);
    }
}

.summary-pills {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.pill {
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;

    &.ok {
        background: var(--ok-soft);
        color: var(--ok);
    }

    &.warn {
        background: var(--warn-soft);
        color: var(--warn);
    }

    &.new {
        background: var(--new-soft);
        color: var(--new);
    }

    &.storno {
        background: var(--storno-soft);
        color: var(--storno);
    }
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.toolbar-left {
    display: flex;
    gap: 8px;
}

.group {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 4px;
    margin-bottom: 16px;
    overflow: hidden;
}

.group-head {
    padding: 10px 16px;
    background: var(--navy);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

table.fields {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    th {
        text-align: left;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #ffffff;
        padding: 8px 16px;
        border-bottom: 1px solid var(--line);
        font-weight: 700;
        background: #8492a0;
    }

    th:nth-child(1),
    td:nth-child(1) {
        width: 180px;
    }

    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3) {
        width: 30%;
    }

    th:nth-child(4),
    td:nth-child(4) {
        width: 60px;
        text-align: center;
    }

    td.status-match {
        color: var(--success, #22c55e);
    }

    td.status-mismatch {
        color: var(--warning, #f97316);
    }

    td {
        padding: 7px 16px;
        border-bottom: 1px solid var(--line);
        vertical-align: middle;
    }

    tbody tr:nth-child(even) {
        background: var(--stripe);
    }

    tr:last-child td {
        border-bottom: none;
    }
}

.field-label {
    font-size: 13px;
    color: var(--ink);
    width: 220px;
    font-weight: 600;
}

.val {
    font-size: 12.5px;
    padding: 5px 8px;
    border-radius: 3px;
    display: inline-block;
    min-height: 14px;
    word-break: break-word;
}

.action-buttons {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--line);
    text-align: right;
}

.potential-matches {
    margin: 20px 0;
    padding: 16px;
    background: var(--ok-soft);
    border: 1px solid var(--ok);
    border-radius: 6px;

    h3 {
        margin: 0 0 12px 0;
        font-size: 16px;
        font-weight: 600;
        color: var(--ok);
    }

    .match-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .match-item {
        padding: 12px;
        background: white;
        border-radius: 4px;
        border: 1px solid var(--line);
    }

    .match-info {
        strong {
            display: block;
            margin-bottom: 4px;
            color: var(--navy);
        }

        .meta {
            font-size: 13px;
            color: #666;

            .exact-badge {
                color: var(--ok);
                font-weight: 600;
            }
        }
    }

    .hint {
        margin: 12px 0 0 0;
        font-size: 13px;
        color: var(--ok);
        font-style: italic;
    }

    &.exact-match {
        background: #e7f4ea;
        border-color: var(--ok);

        h3 {
            color: var(--ok);
        }

        .hint {
            color: var(--ok);
            font-weight: 600;
        }
    }
}
</style>
